import salesforceIcon from '~/assets/public/salesforce1.svg';
import greySalesforceIcon from '~/assets/public/salesforce1-grey.svg';
import { DateTime, Icon, Spinner, Tooltip } from '~/components';
import { useApi, useIntegrations, useToast, useWorkspace } from '~/contexts';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { colors } from '~/styles';
import List from '../../reports/components/List';

const Wrapper = styled.div`
  width: 1rem;
  height: 1rem;
  margin: 0 0.5rem 0 0.5rem;
  cursor: default;
`;

const Image = styled.img`
  min-width: 1rem;
  min-height: 1rem;
  width: 1rem;
`;

const PopoverContainer = styled.div`
  border-radius: 0.3125rem;
  border: solid 1px ${colors.grey10};
  background-color: ${colors.white};
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
  position: relative;
  transition: opacity 50ms ease-out;
  z-index: 300;
  padding: 0.75rem 1rem;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: calc(50% - 0.5rem);
    border: solid 0.5rem transparent;
    border-bottom-color: ${colors.white};
  }
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  border-radius: 0;
  padding: 0;
  margin-right: 0.5rem;
  cursor: pointer;
  color: ${colors.grey25};
  height: auto;
  font-weight: inherit;

  &:hover {
    color: ${colors.primary};
    background: none;
  }
`;

export default function SalesforceIndicator({ message = 'Synced from Salesforce', variant, id, recordTypeName }) {
  const icon = variant === 'grey' ? greySalesforceIcon : salesforceIcon;
  const integrations = useIntegrations();
  const toast = useToast();
  const api = useApi();
  const { workspace } = useWorkspace();
  const [integration, setIntegration] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const hovering = useRef(false);
  const forceVisible = useRef(false);
  const [visible, setVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: 'fixed',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
  });

  const handleCopy = () => {
    if (id) {
      navigator.clipboard.writeText(id);
      toast.success('Salesforce ID copied to clipboard.');
    }
  };

  const handleMouseEnter = () => {
    hovering.current = true;
    setTimeout(() => {
      if (!hovering.current) return;
      setVisible(true);
    }, 100);
  };

  const handleMouseLeave = () => {
    hovering.current = false;
    setTimeout(() => {
      if (!forceVisible.current) setVisible(false);
    }, 200);
  };

  const handlePopoverMouseEnter = () => {
    forceVisible.current = true;
  };

  const handlePopoverMouseLeave = () => {
    forceVisible.current = false;
    setVisible(false);
  };

  const fetchIntegration = useCallback(async () => {
    if (!id && !integrations.salesforce) return;
    try {
      const { data } = await api.www.workspaces(workspace.id).salesforce.get();
      setIntegration(data);
    } finally {
      setIsLoading(false);
    }
  }, [api, workspace, id, integrations.salesforce]);

  useEffect(() => {
    if (id && integrations.salesforce) {
      fetchIntegration();
      return fetchIntegration.cancel;
    }
  }, [fetchIntegration, id, integrations.salesforce]);

  if (!integrations.salesforce) return null;

  if (!id) {
    return (
      <Wrapper>
        <Tooltip message={message}>
          <Image src={icon} />
        </Tooltip>
      </Wrapper>
    );
  }

  return (
    <>
      <Wrapper ref={setReferenceElement} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <Image src={salesforceIcon} />
      </Wrapper>
      {visible &&
        ReactDOM.createPortal(
          <PopoverContainer
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}>
            <List>
              <List.Item label="Synced From">Salesforce</List.Item>
              <List.Item label="Salesforce ID">
                <CopyButton onClick={handleCopy}>
                  <Icon icon="clipboard" />
                </CopyButton>
                {id}
              </List.Item>
              {recordTypeName && <List.Item label="Record Type">{recordTypeName}</List.Item>}
              <List.Item label="Last Sync Date">
                {isLoading ? (
                  <Spinner />
                ) : (
                  <DateTime value={integration?.settings.mostRecentSync} format="l" titleFormat="LL" />
                )}
              </List.Item>
            </List>
          </PopoverContainer>,
          document.body,
        )}
    </>
  );
}
